import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Gauge, Wrench, FileText } from 'lucide-react';
import ContestBanner from './ContestBanner';

const FADE_UP = {
  initial: { opacity: 0, y: 14 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.55, ease: [0.16, 1, 0.3, 1] },
};

export default function StoryPage() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white font-['Outfit']">
      <ContestBanner />

      {/* Back link */}
      <div className="max-w-3xl mx-auto px-6 pt-6">
        <a
          href="/"
          data-testid="story-back-link"
          className="inline-flex items-center gap-2 text-xs text-zinc-400 hover:text-[#FF5E1A] transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back to site
        </a>
      </div>

      {/* Hero */}
      <section className="max-w-3xl mx-auto px-6 pt-10 pb-8" data-testid="story-hero">
        <motion.div {...FADE_UP} className="mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#FF5E1A]/10 border border-[#FF5E1A]/30 rounded-sm">
            <FileText className="w-3.5 h-3.5 text-[#FF5E1A]" />
            <span className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#FF5E1A]">
              Founder Story
            </span>
          </div>
        </motion.div>
        <motion.h1
          {...FADE_UP}
          className="font-black tracking-tighter leading-[0.95] text-4xl sm:text-5xl lg:text-6xl mb-5"
        >
          Why we built<br />
          <span className="bg-gradient-to-r from-[#FF5E1A] via-[#FF9A42] to-[#FFD466] bg-clip-text text-transparent">
            AI Tuner Pro.
          </span>
        </motion.h1>
        <motion.p {...FADE_UP} className="text-base sm:text-lg text-zinc-400 leading-relaxed">
          Todd Miller · Founder · Written from the driveway, after a 3rd-gear pull.
        </motion.p>
      </section>

      {/* Essay */}
      <article
        className="max-w-3xl mx-auto px-6 pb-20 space-y-6 text-[17px] leading-[1.75] text-zinc-300"
        data-testid="story-article"
      >
        <motion.p {...FADE_UP}>
          I paid a shop $600 for a tune. It was pulled from a shared database.
          The car ran — fine. Not great. Not bad. Just fine. I asked what they
          actually changed. Nobody could tell me.
        </motion.p>

        <motion.p {...FADE_UP}>
          That was the last tune I paid for. It was also the day AI Tuner Pro
          started — though I wouldn&apos;t write a line of code for another six
          months.
        </motion.p>

        <motion.p {...FADE_UP}>
          Here&apos;s the problem with how tuning works today. A handful of
          tuners own spreadsheets of maps. They sell you a revision pulled
          from a car that isn&apos;t yours. If it runs, you&apos;re happy. If
          it doesn&apos;t, you pay for another round. Nobody shows their work.
          Nobody shares the before-and-after. Nobody tells you which specific
          cells they touched or why.
        </motion.p>

        <motion.p {...FADE_UP}>
          I wanted the opposite of that. I wanted a tuner that reads YOUR
          binary — the actual bytes out of YOUR ECU — then locates YOUR fuel
          maps, YOUR timing, YOUR lambda targets. Drives with you. Scores
          every pull. Queues the next revision before you even park. Keeps
          going until YOU decide it&apos;s done. Not the shop. Not the clock.
          You.
        </motion.p>

        <motion.div
          {...FADE_UP}
          className="not-prose my-8 p-6 bg-[#111111] border-l-2 border-[#FF5E1A] rounded-sm"
        >
          <div className="flex items-center gap-2 mb-3">
            <Gauge className="w-4 h-4 text-[#FF5E1A]" />
            <span className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#FF5E1A]">
              Proof · not promises
            </span>
          </div>
          <p className="text-base text-zinc-200 leading-relaxed">
            On April 20, 2026, my N51 / MSV80 logged a 3rd-gear pull that peaked at
            <strong className="text-[#FF5E1A]"> 289 HP @ 5,900 RPM</strong>, AFR 13.74,
            knock 1.46V, timing held at 28°. V9 of a tune AI Tuner Pro wrote from
            scratch against my own .bin. That&apos;s real. Not a dyno queen — a daily driver.
          </p>
        </motion.div>

        <motion.p {...FADE_UP}>
          I&apos;m not here to sell you a dream. I&apos;m a solo founder, one
          car, one garage, a pile of .bin files and a lot of coffee. I built
          this because nothing else did what I needed. If that matches what
          YOU need, the door&apos;s open.
        </motion.p>

        <motion.p {...FADE_UP}>
          We cover BMW MSV80 natively today. GM LS (E38/E40/E67/P59/P01),
          BMW E38 M5.2 / ME7.2, and anything unknown all run through
          AI Discovery Mode — the AI asks for the ECU part number, locates
          the tables from first principles, and tells you what it&apos;s
          about to do before it does it. Every change is shown. Every
          datalog is scored. Every tune is versioned. Every stock backup
          is archived forever.
        </motion.p>

        <motion.p {...FADE_UP}>
          Unlimited revisions are included. Not &ldquo;up to five.&rdquo; Not
          &ldquo;fair use.&rdquo; Until YOU Green-Light the tune. That phrase
          is on the pricing page for a reason — it&apos;s the whole
          philosophy compressed into two words.
        </motion.p>

        <motion.div
          {...FADE_UP}
          className="not-prose my-8 p-6 bg-[#111111] border-l-2 border-zinc-600 rounded-sm"
        >
          <div className="flex items-center gap-2 mb-3">
            <Wrench className="w-4 h-4 text-zinc-400" />
            <span className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-zinc-400">
              Honest scope
            </span>
          </div>
          <p className="text-base text-zinc-200 leading-relaxed">
            What AI Tuner Pro <strong>does NOT</strong> do: it does not flash your
            ECU (that&apos;s what the AI Tuner Pro Flasher is for). It does not
            replace a professional tuner if your car needs one — some builds still
            do. It does not promise numbers. Numbers live on the dyno. We promise
            clarity, iteration, and unlimited revisions until you&apos;re the one
            who says done.
          </p>
        </motion.div>

        <motion.p {...FADE_UP}>
          If you&apos;ve ever paid for a tune and wondered what you actually
          paid for — this is for you. If you&apos;ve ever wanted to see the
          math before you flash — this is for you. If you&apos;ve been
          burned by a shared-database tune that felt flat or broken or
          rich under boost — this is for you.
        </motion.p>

        <motion.p {...FADE_UP} className="text-white font-medium">
          We&apos;re not pulling maps from a spreadsheet. We&apos;re writing
          them from your binary. That&apos;s the whole difference. Everything
          else we do flows from that one decision.
        </motion.p>

        <motion.div {...FADE_UP} className="not-prose pt-8 border-t border-[#27272A] flex flex-col sm:flex-row gap-3 items-start">
          <a
            href="/green-light"
            data-testid="story-cta-pricing"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF5E1A] hover:bg-[#FF7A42] text-white text-xs font-bold tracking-[0.12em] uppercase rounded-sm transition-all shadow-[0_0_24px_rgba(255,94,26,0.35)]"
          >
            Start a tune — from $250
          </a>
          <a
            href="/"
            data-testid="story-cta-home"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#27272A] hover:bg-[#3a3a3d] text-white text-xs font-bold tracking-[0.12em] uppercase rounded-sm transition-all"
          >
            Back to site
          </a>
        </motion.div>

        <motion.p
          {...FADE_UP}
          className="text-[11px] text-zinc-600 pt-6 font-['JetBrains_Mono']"
        >
          — Todd Miller, Founder. AI Tuner Pro · aitunerproportal.com
        </motion.p>
      </article>
    </div>
  );
}
